import type { GetEnvelopeSubjectsThemesQuery, GetSubjectsThemesQuery } from "api/generated";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

export interface IEnvelopeTheme {
  themeName: string;
  id: string | undefined;
  envelopes: {
    red: number | null;
    yellow: number | null;
    green: number | null;
  };
}

export interface ISubjectData {
  subjectName: string;
  subjectId: string;
  assignmentsCount?: number;
  themes: IEnvelopeTheme[];
  envelopes: {
    red: number | null;
    yellow: number | null;
    green: number | null;
  };
}

export const sortGeneralSubjects = (subjectsData: GetSubjectsThemesQuery) => {
  const parsedData: ISubjectData[] = [];
  const themesBySubject = new Map<string, IEnvelopeTheme[]>();

  subjectsData.userThemes.forEach(userTheme => {
    const subjectId = userTheme.theme?.subject;
    if (!subjectId) return;

    const subjectThemes = themesBySubject.get(subjectId) ?? [];
    const parsedTheme = parseTheme(userTheme);
    if (parsedTheme) {
      subjectThemes.push(parsedTheme);
      themesBySubject.set(subjectId, subjectThemes);
    }
  });

  for (const subject of subjectsData.subjectsCached) {
    if (subject.isFavorite === true && typeof subject.appName === "string") {
      const userSubject = subjectsData.userSubjectsCached.find(
        subjectPercentage => subjectPercentage.subject?.id === subject.id,
      );

      parsedData.push({
        subjectName: subject.appName,
        subjectId: subject.id,
        themes: themesBySubject.get(subject.id) ?? [],
        envelopes: {
          red: userSubject?.assignmentsRed ?? 0,
          yellow: userSubject?.assignmentsYellow ?? 0,
          green: userSubject?.assignmentsGreen ?? 0,
        },
      });
    }
  }

  parsedData.sort((a, b) => a.subjectName.localeCompare(b.subjectName));

  return parsedData;
};

export const sortEnvelopeSubjects = (subjectsData: GetEnvelopeSubjectsThemesQuery, envelopeId?: string) => {
  const parsedData: ISubjectData[] = [];

  for (const subject of subjectsData.subjects) {
    if (subject.isFavorite === true && typeof subject.appName === "string") {
      const userSubject = subjectsData.userSubjects.find(
        subjectPercentage => subjectPercentage.subject?.id === subject.id,
      );

      const themes = [...subjectsData.userThemes]
        .map(theme => {
          switch (envelopeId) {
            case "cervena":
              return parseThemeRedEnvelope(theme);
            case "oranzova":
              return parseThemeOrangeEnvelope(theme);
            case "zelena":
              return parseThemeGreenEnvelope(theme);
            default:
              return null;
          }
        })
        .filter(BooleanWithTypeGuard)
        .filter(theme => theme.subject === subject.id)
        .filter(theme => theme.envelopes.red ?? theme.envelopes.yellow ?? theme.envelopes.green)
        .sort((a, b) => a.themeName.localeCompare(b.themeName));

      const envelopeCounts = {
        red: 0,
        yellow: 0,
        green: 0,
      };

      for (const theme of themes) {
        envelopeCounts.red += theme.envelopes.red ?? 0;
        envelopeCounts.yellow += theme.envelopes.yellow ?? 0;
        envelopeCounts.green += theme.envelopes.green ?? 0;
      }

      parsedData.push({
        subjectName: subject.appName,
        subjectId: subject.id,
        assignmentsCount:
          userSubject?.assignmentsGreen ?? userSubject?.assignmentsRed ?? userSubject?.assignmentsYellow,
        themes,
        envelopes: envelopeCounts,
      });
    }
  }

  parsedData.sort((a, b) => a.subjectName.localeCompare(b.subjectName));

  return parsedData;
};

const parseThemeRedEnvelope = (
  theme:
    | {
        hasAssignments: boolean;
        assignmentsRed: number;
        assignmentsGreen: number;
        assignmentsYellow: number;
        id: string;
        theme?: {
          appName?: string | null;
          name?: string | null;
          description?: string | null;
          subject?: string | null;
          avgTestPoints?: number | null;
        } | null;
      }
    | null
    | undefined,
) => {
  if (!theme) return null;

  return {
    themeName: theme.theme?.appName ?? theme.theme?.name ?? "",
    id: theme.id,
    subject: theme.theme?.subject ?? null,
    envelopes: {
      red: theme.assignmentsRed,
      yellow: null,
      green: null,
    },
  };
};

const parseThemeOrangeEnvelope = (
  theme:
    | {
        hasAssignments: boolean;
        assignmentsRed: number;
        assignmentsGreen: number;
        assignmentsYellow: number;
        id: string;
        theme?: {
          appName?: string | null;
          name?: string | null;
          description?: string | null;
          subject?: string | null;
          avgTestPoints?: number | null;
        } | null;
      }
    | null
    | undefined,
) => {
  if (!theme) return null;

  return {
    themeName: theme.theme?.appName ?? theme.theme?.name ?? "",
    id: theme.id,
    subject: theme.theme?.subject ?? null,
    envelopes: {
      red: null,
      yellow: theme.assignmentsYellow,
      green: null,
    },
  };
};

const parseThemeGreenEnvelope = (
  theme:
    | {
        hasAssignments: boolean;
        assignmentsRed: number;
        assignmentsGreen: number;
        assignmentsYellow: number;
        id: string;
        theme?: {
          appName?: string | null;
          name?: string | null;
          description?: string | null;
          subject?: string | null;
          avgTestPoints?: number | null;
        } | null;
      }
    | null
    | undefined,
) => {
  if (!theme) return null;

  return {
    themeName: theme.theme?.appName ?? theme.theme?.name ?? "",
    id: theme.id,
    subject: theme.theme?.subject ?? null,
    envelopes: {
      red: null,
      yellow: null,
      green: theme.assignmentsGreen,
    },
  };
};

export const parseTheme = (
  theme:
    | {
        hasAssignments: boolean;
        assignmentsRed: number;
        assignmentsGreen: number;
        assignmentsYellow: number;
        id: string;
        theme?: {
          appName?: string | null;
          name?: string | null;
          description?: string | null;
          subject?: string | null;
          avgTestPoints?: number | null;
        } | null;
      }
    | null
    | undefined,
) => {
  if (!theme) return null;

  return {
    themeName: theme.theme?.appName ?? theme.theme?.name ?? "",
    id: theme.id,
    subject: theme.theme?.subject ?? null,
    envelopes: {
      red: theme.assignmentsRed,
      yellow: theme.assignmentsYellow,
      green: theme.assignmentsGreen,
    },
  };
};
